import { Checkbox, Dialog, TextInput, useToast } from '@ordr-as/umami-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    PaymentMethodCategory,
    PaymentMethodConfiguration,
    PaymentMethodOverridableConfiguration,
    PaymentProvider,
    PayoutSource,
} from '../../generated-api'
import { useCreatePaymentMethod } from 'hooks/usePaymentMethodApi'

type CreatePaymentMethodDialogProps = {
    dialogIsOpen: boolean
    onClose: () => void
}

const CreatePaymentMethodDialog = (props: CreatePaymentMethodDialogProps) => {
    const { dialogIsOpen, onClose } = props

    const [paymentProvider, setPaymentProvider] = useState<PaymentProvider>(PaymentProvider.UNINTEGRATED)
    const [paymentMethod, setPaymentMethod] = useState('')
    const [paymentMethodCategory, setPaymentMethodCategory] = useState<PaymentMethodCategory>(
        PaymentMethodCategory.UNCLASSIFIED
    )
    const [payoutSource, setPayoutSource] = useState<PayoutSource>(PayoutSource.FAVRIT)
    const [visibleInApps, setVisibleInApps] = useState(true)
    const [paymentMethodDisplayLabel, setPaymentMethodDisplayLabel] = useState('')

    const toast = useToast()
    const navigate = useNavigate()
    const { mutate: runCreatePaymentMethod, isLoading: isCreating } = useCreatePaymentMethod()

    const handleCreate = () => {
        if (!paymentMethod.trim()) {
            toast.pushError({
                id: 'create-payment-method-no-name',
                message: 'Payment method name is required',
            })
            return
        }

        const overridableConfiguration: PaymentMethodOverridableConfiguration = {
            paymentMethodDisplayLabel: paymentMethodDisplayLabel.trim() || undefined,
            unintegratedFieldsSchema: undefined,
            cardSchemesConfiguration: undefined,
        }

        const configuration: PaymentMethodConfiguration = {
            visibleInApps,
            overridableConfiguration,
        }

        runCreatePaymentMethod(
            {
                paymentProvider,
                paymentMethod: paymentMethod.trim(),
                paymentMethodCategory,
                payoutSource,
                _configuration: configuration,
            },
            {
                onSuccess: () => {
                    toast.pushSuccess({
                        id: 'create-payment-method-success',
                        message: `Successfully created payment method ${paymentMethod}`,
                    })
                    handleClose()
                    // Navigate to the newly created payment method details page
                    navigate(`/payment-methods/${paymentProvider}/${paymentMethod.trim()}/details`)
                },
                onError: (error) => {
                    console.error('Failed to create payment method:', error)
                    toast.pushError({
                        id: 'create-payment-method-error',
                        message: 'Failed to create payment method',
                    })
                },
            }
        )
    }

    const handleClose = () => {
        setPaymentProvider(PaymentProvider.UNINTEGRATED)
        setPaymentMethod('')
        setPaymentMethodCategory(PaymentMethodCategory.UNCLASSIFIED)
        setPayoutSource(PayoutSource.FAVRIT)
        setVisibleInApps(true)
        setPaymentMethodDisplayLabel('')
        onClose()
    }

    return (
        <>
            {dialogIsOpen && (
                <Dialog
                    title="Create New Payment Method"
                    onClose={handleClose}
                    actionLabel={isCreating ? 'Creating...' : 'Create'}
                    onAction={handleCreate}
                    secondaryActionLabel="Cancel"
                    onSecondaryAction={handleClose}
                    isDisabledAction={!paymentMethod.trim() || isCreating}
                    isDisabledSecondaryAction={isCreating}
                >
                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Payment Provider *
                            </label>
                            <select
                                className="w-full border border-grey-light rounded p-2"
                                value={paymentProvider}
                                onChange={(e) => setPaymentProvider(e.target.value as PaymentProvider)}
                            >
                                <option value={PaymentProvider.UNINTEGRATED}>Unintegrated</option>
                                <option value={PaymentProvider.ADYEN}>Adyen</option>
                                <option value={PaymentProvider.SOFTPAY}>SoftPay</option>
                                <option value={PaymentProvider.NETS}>Nets</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Payment Method Name *
                            </label>
                            <TextInput
                                value={paymentMethod}
                                onChange={(value: string) => setPaymentMethod(value.toUpperCase())}
                                placeholder="e.g., CARD, VIPPS, GIFT_CARD"
                                fullWidth
                            />
                            <p className="text-small text-grey-dark mt-1">
                                Use uppercase with underscores (e.g., GIFT_CARD)
                            </p>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Payment Method Category *
                            </label>
                            <select
                                className="w-full border border-grey-light rounded p-2"
                                value={paymentMethodCategory}
                                onChange={(e) => setPaymentMethodCategory(e.target.value as PaymentMethodCategory)}
                            >
                                <option value={PaymentMethodCategory.GIFT_CARD}>Gift Card</option>
                                <option value={PaymentMethodCategory.FOOD_DELIVERY_SERVICE}>
                                    Food Delivery Service
                                </option>
                                <option value={PaymentMethodCategory.UNCLASSIFIED}>Unclassified</option>
                                <option value={PaymentMethodCategory.LOYALTY_CASH_POINTS}>Loyalty Cash Points</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Payout Source *
                            </label>
                            <select
                                className="w-full border border-grey-light rounded p-2"
                                value={payoutSource}
                                onChange={(e) => setPayoutSource(e.target.value as PayoutSource)}
                            >
                                <option value={PayoutSource.FAVRIT}>Favrit</option>
                                <option value={PayoutSource.ADYEN}>Adyen</option>
                                <option value={PayoutSource.UNKNOWN}>Unknown</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Display Label (Optional)
                            </label>
                            <TextInput
                                value={paymentMethodDisplayLabel}
                                onChange={(value: string) => setPaymentMethodDisplayLabel(value)}
                                placeholder="e.g., Gift Card, Credit Card"
                                fullWidth
                            />
                            <p className="text-small text-grey-dark mt-1">
                                User-friendly label shown in apps (can be overridden per location)
                            </p>
                        </div>

                        <div>
                            <Checkbox isSelected={visibleInApps} onChange={setVisibleInApps}>
                                Visible in Apps
                            </Checkbox>
                            <p className="text-small text-grey-dark mt-1">
                                If enabled, the payment method will be shown in apps where it is enabled
                            </p>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    )
}

export default CreatePaymentMethodDialog
